import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

/**
 * Portlet Configuration Service
 * Admin service for managing portlet configurations
 */

// Shown when the backend returns 403 (requires the Root role). The axios
// wrapper suppresses 401/403 into an empty 200, so without the
// suppressedAuthStatus check the page would show a generic load error.
const ADMIN_ACCESS_ERROR =
  "You need administrator access to view this page. Please log in with an administrator account.";

export interface PortletIcon {
  type: "emoji" | "url" | "key" | "lucide";
  value?: string; // emoji char, image URL, or lucide icon name (kebab-case, e.g. "graduation-cap")
  key?: string;
  color?: string; // optional icon color (lucide only). Omit to inherit the default (currentColor).
}

export interface PortletRoute {
  path: string;
}

export interface PortletChild {
  id: string;
  title: string;
  url: string;
  description?: string;
  icon?: PortletIcon;
  gradient?: string;
  openInNewTab?: boolean;
}

export interface Portlet {
  id: string;
  type: "standard" | "link" | "clone" | "widget" | "group";
  title: string;
  description: string;
  icon: PortletIcon;
  gradient?: string;
  visible: boolean;
  roleIds: number[];
  order: number;
  category: string;
  // Type-specific fields
  route?: PortletRoute;           // standard
  url?: string;                   // link
  openInNewTab?: boolean;         // link
  sourceId?: string;              // clone
  queryParams?: Record<string, string>; // clone
  widgetType?: "iframe" | "chart" | "counter" | "list"; // widget
  widgetConfig?: Record<string, unknown>; // widget
  children?: PortletChild[];      // group
}

export interface GetPortletConfigResponse {
  success: boolean;
  data?: {
    customerId: number;
    portlets: Portlet[];
    categories: string[];
  };
  error?: string;
}

export interface UpdatePortletConfigResponse {
  success: boolean;
  message?: string;
  data?: {
    portlets: Portlet[];
  };
  error?: string;
}

export interface AddPortletResponse {
  success: boolean;
  message?: string;
  data?: Portlet;
  error?: string;
}

export interface DeletePortletResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface Role {
  id: number;
  name: string;
}

export interface GetRolesResponse {
  success: boolean;
  data?: Role[];
  error?: string;
}

// iLearn portlet types
export interface ILearnPortlet {
  id: string;
  type: "standard" | "link";
  title: string;
  description: string;
  // Either a PortletIcon object (emoji/key/url) or a plain string path like
  // "./assets/imgs/learningmodules.png" — iLearn renders the string as an
  // <img src> directly.
  icon: PortletIcon | string;
  gradient?: string;
  visible: boolean;
  order: number;
  category: string;
  // Role gating — empty means visible to all. Matches the iLearn ACL JSON,
  // where every portlet sample carries this field.
  roleIds?: number[];
  route?: PortletRoute;
  params?: Record<string, any>;
  url?: string;
  openInNewTab?: boolean;
  isTile?: boolean;
  isMenu?: boolean;
  menuIcon?: string;
  enableForEnrollment?: boolean;
  enableForParent?: boolean;
  enableForEnquiry?: boolean;
  courseOrProgramIds?: number[];
}

export interface GetILearnPortletConfigResponse {
  success: boolean;
  data?: {
    customerId: number;
    portlets: ILearnPortlet[];
    categories: string[];
  };
  error?: string;
}

export interface UpdateILearnPortletConfigResponse {
  success: boolean;
  message?: string;
  data?: { portlets: ILearnPortlet[] };
  error?: string;
}

export interface AddILearnPortletResponse {
  success: boolean;
  message?: string;
  data?: ILearnPortlet;
  error?: string;
}

class PortletConfigService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/portlets`;
  }

  /**
   * Get full portlet configuration for admin
   */
  async getPortletConfig(): Promise<GetPortletConfigResponse> {
    try {
      const response = await myAxios.get<GetPortletConfigResponse>(
        `${this.baseUrl}/config`
      );
      if ((response as any).suppressedAuthStatus === 403) {
        return { success: false, error: ADMIN_ACCESS_ERROR };
      }
      return response.data;
    } catch (error: any) {
      console.error("[PortletConfigService] Error fetching config:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to fetch portlet configuration",
      };
    }
  }

  /**
   * Update all portlets (reorder, visibility, etc.)
   */
  async updatePortletConfig(portlets: Portlet[]): Promise<UpdatePortletConfigResponse> {
    try {
      const response = await myAxios.put(
        `${this.baseUrl}/config`,
        { portlets }
      );
      return response.data as unknown as UpdatePortletConfigResponse;
    } catch (error: any) {
      console.error("[PortletConfigService] Error updating config:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to update portlet configuration",
      };
    }
  }

  /**
   * Add a new portlet
   */
  async addPortlet(portlet: Partial<Portlet>): Promise<AddPortletResponse> {
    try {
      const response = await myAxios.post(
        `${this.baseUrl}/config/add`,
        portlet
      );
      return response.data as unknown as AddPortletResponse;
    } catch (error: any) {
      console.error("[PortletConfigService] Error adding portlet:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to add portlet",
      };
    }
  }

  /**
   * Delete a portlet
   */
  async deletePortlet(portletId: string): Promise<DeletePortletResponse> {
    try {
      const response = await myAxios.delete<DeletePortletResponse>(
        `${this.baseUrl}/config/${portletId}`
      );
      return response.data;
    } catch (error: any) {
      console.error("[PortletConfigService] Error deleting portlet:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to delete portlet",
      };
    }
  }

  /**
   * Get active roles for portlet role assignment
   */
  async getRoles(): Promise<GetRolesResponse> {
    try {
      const response = await myAxios.get<GetRolesResponse>(
        `${this.baseUrl}/roles`
      );
      return response.data;
    } catch (error: any) {
      console.error("[PortletConfigService] Error fetching roles:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to fetch roles",
      };
    }
  }

  /**
   * Reorder portlets - convenience method
   */
  async reorderPortlets(portlets: Portlet[], fromIndex: number, toIndex: number): Promise<UpdatePortletConfigResponse> {
    const reordered = [...portlets];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed);

    // Update order values
    const updatedPortlets = reordered.map((p, index) => ({
      ...p,
      order: index,
    }));

    return this.updatePortletConfig(updatedPortlets);
  }

  /**
   * Toggle portlet visibility
   */
  async toggleVisibility(portlets: Portlet[], portletId: string): Promise<UpdatePortletConfigResponse> {
    const updatedPortlets = portlets.map((p) =>
      p.id === portletId ? { ...p, visible: !p.visible } : p
    );
    return this.updatePortletConfig(updatedPortlets);
  }

  // ── iLearn Portlet Config Methods ──

  async getILearnPortletConfig(): Promise<GetILearnPortletConfigResponse> {
    try {
      const response = await myAxios.get<GetILearnPortletConfigResponse>(
        `${this.baseUrl}/ilearn-config`
      );
      if ((response as any).suppressedAuthStatus === 403) {
        return { success: false, error: ADMIN_ACCESS_ERROR };
      }
      return response.data;
    } catch (error: any) {
      console.error("[PortletConfigService] Error fetching iLearn config:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to fetch iLearn portlet configuration",
      };
    }
  }

  async updateILearnPortletConfig(portlets: ILearnPortlet[]): Promise<UpdateILearnPortletConfigResponse> {
    try {
      const response = await myAxios.put(
        `${this.baseUrl}/ilearn-config`,
        { portlets }
      );
      return response.data as unknown as UpdateILearnPortletConfigResponse;
    } catch (error: any) {
      console.error("[PortletConfigService] Error updating iLearn config:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to update iLearn portlet configuration",
      };
    }
  }

  async addILearnPortlet(portlet: Partial<ILearnPortlet>): Promise<AddILearnPortletResponse> {
    try {
      const response = await myAxios.post(
        `${this.baseUrl}/ilearn-config/add`,
        portlet
      );
      return response.data as unknown as AddILearnPortletResponse;
    } catch (error: any) {
      console.error("[PortletConfigService] Error adding iLearn portlet:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to add iLearn portlet",
      };
    }
  }

  async deleteILearnPortlet(portletId: string): Promise<DeletePortletResponse> {
    try {
      const response = await myAxios.delete<DeletePortletResponse>(
        `${this.baseUrl}/ilearn-config/${portletId}`
      );
      return response.data;
    } catch (error: any) {
      console.error("[PortletConfigService] Error deleting iLearn portlet:", error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.error || "Failed to delete iLearn portlet",
      };
    }
  }

  /**
   * Generate unique portlet ID
   */
  generatePortletId(type: string, title: string): string {
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const timestamp = Date.now().toString(36);
    return `${type}-${slug}-${timestamp}`;
  }
}

export default new PortletConfigService();
